import { createFileRoute } from "@tanstack/react-router";
import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { useServerFn } from "@tanstack/react-start";
import { getCostToServe } from "@/lib/cost-to-serve.functions";
import { useScope } from "@/hooks/use-scope";
import { PageHeader } from "@/components/ui/page-header";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";

export const Route = createFileRoute("/_authenticated/app/admin/cost-to-serve")({ component: CostToServePage });

const fmt = (cents: number) => `$${(cents / 100).toLocaleString(undefined, { maximumFractionDigits: 2 })}`;

function CostToServePage() {
  const { tenantId } = useScope();
  const [months, setMonths] = useState(3);
  const fn = useServerFn(getCostToServe);
  const { data, isLoading } = useQuery({
    queryKey: ["cost-to-serve", tenantId, months],
    enabled: !!tenantId,
    queryFn: () => fn({ data: { tenantId: tenantId!, months } }),
  });

  const rows = data?.rows ?? [];
  const totals = rows.reduce((acc, r) => ({
    revenue: acc.revenue + r.revenue_cents,
    cost: acc.cost + r.total_cost_cents,
    margin: acc.margin + r.margin_cents,
  }), { revenue: 0, cost: 0, margin: 0 });

  return (
    <>
      <PageHeader
        title="Cost-To-Serve"
        description="Margin per customer per month — revenue minus label, consumable, and labor costs."
        actions={
          <Select value={String(months)} onValueChange={(v) => setMonths(Number(v))}>
            <SelectTrigger className="w-[140px]"><SelectValue /></SelectTrigger>
            <SelectContent>
              {[1, 3, 6, 12].map((m) => <SelectItem key={m} value={String(m)}>Last {m}mo</SelectItem>)}
            </SelectContent>
          </Select>
        }
      />
      <div className="px-4 sm:px-6 py-4 grid gap-3 sm:grid-cols-3">
        <Tile label="Revenue" value={fmt(totals.revenue)} />
        <Tile label="Total cost" value={fmt(totals.cost)} />
        <Tile label="Margin" value={fmt(totals.margin)} accent={totals.margin >= 0 ? "ok" : "warn"} />
      </div>
      <div className="px-4 sm:px-6 pb-6">
        <Card>
          <CardHeader><CardTitle className="text-base">Per-Customer Breakdown</CardTitle></CardHeader>
          <CardContent className="p-0 overflow-x-auto">
            {isLoading ? (
              <div className="p-6 text-sm text-center text-muted-foreground">Loading…</div>
            ) : rows.length === 0 ? (
              <div className="p-6 text-sm text-center text-muted-foreground">No invoiced activity yet.</div>
            ) : (
              <table className="w-full text-sm">
                <thead className="bg-muted/40 text-xs text-muted-foreground">
                  <tr>
                    <th className="text-left p-3">Period</th>
                    <th className="text-left p-3">Customer</th>
                    <th className="text-right p-3">Revenue</th>
                    <th className="text-right p-3">Labels</th>
                    <th className="text-right p-3">Consumables</th>
                    <th className="text-right p-3">Labor</th>
                    <th className="text-right p-3">Margin</th>
                    <th className="text-right p-3">Margin %</th>
                  </tr>
                </thead>
                <tbody>
                  {rows.map((r, i) => (
                    <tr key={i} className="border-t">
                      <td className="p-3 font-mono text-xs">{r.period_month?.slice(0, 7)}</td>
                      <td className="p-3">{r.customer_name ?? "—"}</td>
                      <td className="p-3 text-right tabular-nums">{fmt(r.revenue_cents)}</td>
                      <td className="p-3 text-right tabular-nums text-muted-foreground">{fmt(r.label_cost_cents)}</td>
                      <td className="p-3 text-right tabular-nums text-muted-foreground">{fmt(r.consumable_cost_cents)}</td>
                      <td className="p-3 text-right tabular-nums text-muted-foreground">{fmt(r.labor_cost_cents)}</td>
                      <td className="p-3 text-right tabular-nums font-medium">{fmt(r.margin_cents)}</td>
                      <td className="p-3 text-right">
                        {r.margin_pct == null ? "—" : (
                          <Badge variant="outline" className={r.margin_pct >= 0 ? "text-success" : "text-warning"}>
                            {r.margin_pct}%
                          </Badge>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </CardContent>
        </Card>
      </div>
    </>
  );
}

function Tile({ label, value, accent }: { label: string; value: string; accent?: "ok" | "warn" }) {
  return (
    <Card>
      <CardContent className="p-4">
        <div className="text-xs text-muted-foreground">{label}</div>
        <div className={`mt-1 text-2xl font-semibold tabular-nums ${
          accent === "ok" ? "text-success" :
          accent === "warn" ? "text-warning" : ""
        }`}>{value}</div>
      </CardContent>
    </Card>
  );
}
